import styled from '@emotion/styled';

import { pageContentWidth } from '@/styles/pageLayout';

export const Container = styled.div`
	${pageContentWidth}
	display: flex;
	flex-direction: column;
	gap: 4.8rem;
	padding-top: calc(${(props) => props.theme.heights.header} + 8rem);
	padding-bottom: 8rem;
	@media screen and (max-width: ${(props) => props.theme.scale.contentBreakpoint}) {
		padding-left: ${(props) => props.theme.scale.contentPaddingX};
		padding-right: ${(props) => props.theme.scale.contentPaddingX};
	}
`;

export const Aside = styled.aside`
	display: flex;
	flex-direction: column;
	max-width: 24rem;
	width: 100%;
	.info {
		display: flex;
		flex-direction: column;
		gap: 1.2rem;
		padding: 2.4rem 2rem 1.6rem 2rem;
		background-color: ${(props) => props.theme.colors.primary400};
		color: #fff;
		.title {
			font-size: ${(props) => props.theme.fontSize.sizeXl};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
			line-height: ${(props) => props.theme.ui.lineHeight.heading};
			letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
		}
	}
`;

export const LogoutButton = styled.button`
	cursor: pointer;
	display: flex;
	align-items: center;
	background-color: transparent;
	border: none;
	color: #fff;
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.lineHeight.tight};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	height: 3.2rem;
`;

export const MenuBoxList = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0.8rem 0;
`;

export const MenuBox = styled.div`
	background-color: #f8f9fa;
	color: #868e96;
	padding: 1.6rem 2rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	cursor: pointer;
	&.active {
		color: ${(props) => props.theme.colors.primary400};
		border-right: 0.4rem solid ${(props) => props.theme.colors.primary400};
	}
`;

export const Main = styled.div`
	display: flex;
	gap: 4rem;
`;

export const MainContent = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;
`;

export const Title = styled.h1`
	font-size: ${(props) => props.theme.fontSize.size4xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

/* ── Mobile full-screen modal ── */

export const MobileModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 100;
	display: flex;
	flex-direction: column;
	height: 100dvh;
	overflow: hidden;
	background-color: ${(props) => props.theme.ui.surface.page};
`;

export const MobileModalHeader = styled.header`
	display: grid;
	grid-template-columns: 8rem 1fr 8rem;
	align-items: center;
	flex-shrink: 0;
	height: ${(props) => props.theme.heights.headerMobile};
	padding: 0 ${(props) => props.theme.scale.spaceSm};
	border-bottom: 1px solid ${(props) => props.theme.ui.border.default};
	background-color: ${(props) => props.theme.ui.surface.page};
`;

export const ModalHeaderSide = styled.div<{ $align?: 'start' | 'end' }>`
	display: flex;
	align-items: center;
	justify-content: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};
	min-width: 0;
`;

export const ModalBackButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${(props) => props.theme.scale.space3xs};
	padding: ${(props) => props.theme.scale.space2xs};
	border: none;
	background: transparent;
	color: ${(props) => props.theme.ui.text.secondary};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	font-family: inherit;
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		color: ${(props) => props.theme.ui.interactive.primary};
	}
`;

export const ModalHomeButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 4rem;
	height: 4rem;
	padding: 0;
	border: 1px solid ${(props) => props.theme.ui.border.default};
	border-radius: ${(props) => props.theme.effects.radiusSm};
	background: ${(props) => props.theme.ui.surface.page};
	color: ${(props) => props.theme.ui.text.secondary};
	cursor: pointer;

	&:hover {
		color: ${(props) => props.theme.ui.interactive.primary};
		border-color: ${(props) => props.theme.ui.interactive.primary};
		background-color: ${(props) => props.theme.ui.interactive.primaryMuted};
	}
`;

export const MobileModalTitle = styled.h1`
	margin: 0;
	padding: 0 ${(props) => props.theme.scale.space2xs};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	color: ${(props) => props.theme.ui.text.primary};
	text-align: center;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const MobileModalBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	padding: ${(props) => props.theme.scale.spaceMd};
`;

export const MobileHub = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: ${(props) => props.theme.scale.spaceLg};
`;

export const MobileProfileCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.scale.spaceMd};
	padding: ${(props) => props.theme.scale.spaceLg};
	border-radius: ${(props) => props.theme.effects.radiusMd};
	background-color: ${(props) => props.theme.colors.primary400};
	color: ${(props) => props.theme.ui.text.inverse};
`;

export const MobileProfileName = styled.p`
	margin: 0;
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const MobileLogoutButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: ${(props) => props.theme.scale.space3xs};
	align-self: flex-start;
	padding: 0;
	border: none;
	background: transparent;
	color: inherit;
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	font-family: inherit;
	cursor: pointer;
`;

export const MobileMenuList = styled.ul`
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0;
	list-style: none;
	border: 1px solid ${(props) => props.theme.ui.border.default};
	border-radius: ${(props) => props.theme.effects.radiusMd};
	overflow: hidden;
`;

export const MobileMenuItem = styled.li`
	border-bottom: 1px solid ${(props) => props.theme.ui.border.default};

	&:last-of-type {
		border-bottom: none;
	}
`;

export const MobileMenuButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 5.2rem;
	padding: ${(props) => props.theme.scale.spaceSm} ${(props) => props.theme.scale.spaceMd};
	border: none;
	background: ${(props) => props.theme.ui.surface.page};
	color: ${(props) => props.theme.ui.text.primary};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	font-family: inherit;
	text-align: left;
	cursor: pointer;

	&:hover {
		background-color: ${(props) => props.theme.ui.interactive.hoverWash};
	}
`;

export const MobileDetailContent = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	min-width: 0;

	& > form,
	& > div {
		width: 100%;
		max-width: none;
	}
`;
